import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Row, Col, Button, List, Checkbox, Spin, Tabs, Popover, Typography, Descriptions, Badge } from 'antd';
import { CloseOutlined, FilterOutlined, UnorderedListOutlined, ExportOutlined } from '@ant-design/icons';
import Filter from './Filter';

const { Link, Title } = Typography;

const CertificationBrowseCSPModalForm = ({ open, onCancel, onLoad, dbCertifications, rsSkillsGroup, rsSpecificKnowledge, rsSpecificSkills }) => {
  const nonArchived = useMemo(() => (Array.isArray(dbCertifications) ? dbCertifications.filter((c) => !c.archived) : []), [dbCertifications]);

  const [filteredList, setFilteredList] = useState(nonArchived);
  const [checkedList, setCheckedList] = useState([]);
  const [selectedCert, setSelectedCert] = useState(null);
  const [resetFlag, setResetFlag] = useState(false);

  useEffect(() => {
    setFilteredList(nonArchived);
    setCheckedList([]);
    setSelectedCert(null);
  }, [nonArchived, open]);

  const resetFilter = () => {
    setFilteredList(nonArchived);
    setResetFlag((prev) => !prev);
  };

  const handleFilterChange  = useCallback((filtered) => {
    const list = Array.isArray(filtered) ? filtered.filter((c) => !c.archived) : [];
    setFilteredList(list);
  }, [setFilteredList]);

  const onCheckboxChange = (id, cert) => {
    setCheckedList((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setSelectedCert(cert);
  };

  const tabsItems = selectedCert
    ? [
        {
          key: 'overview',
          label: 'Overview',
          children: (
            <>
              <h5>Description</h5>
              <p>{selectedCert.description}</p>
              <Button icon={<ExportOutlined />} href={selectedCert.link} target="_blank">
                Website
              </Button>
              <h5 className="rewire-title">Rewire skill groups</h5>
              {selectedCert.skills_group
                .sort((a, b) => rsSkillsGroup.find((g) => g.id === a).name.localeCompare(rsSkillsGroup.find((g) => g.id === b).name))
                .map((groupId) => {
                  const grp = rsSkillsGroup.find((g) => g.id === groupId);
                  return (
                    <Popover
                      getPopupContainer={() => document.body}
                      overlayStyle={{
                        position: 'fixed',
                        overflow: 'visible',
                      }}
                      key={grp.id}
                      content={
                        <div className="skills-popover">
                          <Title level={4}>{grp.name}</Title>
                          <Title level={5}>Included Skills</Title>
                          <ul>{grp.specific_skills.length > 0 ? grp.specific_skills.map((sg, i) => <li key={i}>{rsSpecificSkills.find((e) => e.id === sg)?.name}</li>) : <span className="empty">None</span>}</ul>
                          <Title level={5}>Included Knowledge</Title>
                          <ul>{grp.specific_knowledge.length > 0 ? grp.specific_knowledge.map((sg, i) => <li key={i}>{rsSpecificKnowledge.find((e) => e.id === sg)?.name}</li>) : <span className="empty">None</span>}</ul>
                        </div>
                      }
                    >
                      <Badge count={grp.name} color={grp.color || 'white'} key={grp.id} className="skillBadge" />
                    </Popover>
                  );
                })}
            </>
          ),
        },
        {
          key: 'details',
          label: 'Details',
          children: (
            <div className="details-content-scroll">
              <Descriptions bordered column={1} layout="horizontal" size='small'>
                <Descriptions.Item label="Organization">{selectedCert.organization}</Descriptions.Item>
                <Descriptions.Item label="Main Topics">{selectedCert.main_topics}</Descriptions.Item>
                <Descriptions.Item label="Language">{Array.isArray(selectedCert.language) ? selectedCert.language.join(', ') : selectedCert.language}</Descriptions.Item>
                <Descriptions.Item label="Level">{selectedCert.level}</Descriptions.Item>
                <Descriptions.Item label="Duration">{selectedCert.duration}</Descriptions.Item>
                <Descriptions.Item label="e-Competences">{(selectedCert.role_of_the_ecsf || []).join(', ')}</Descriptions.Item>
                <Descriptions.Item label="ISO 17024">{selectedCert.iso_17024 ? 'Yes' : 'No'}</Descriptions.Item>
                <Descriptions.Item label="Domain">{Array.isArray(selectedCert.domain) ? selectedCert.domain.join(', ') : selectedCert.domain}</Descriptions.Item>
                <Descriptions.Item label="Prerequisites">{selectedCert.prerequisites}</Descriptions.Item>
                <Descriptions.Item label="Type/Format">{selectedCert.type_format}</Descriptions.Item>
                <Descriptions.Item label="Price (€)">{selectedCert.price}</Descriptions.Item>
              </Descriptions>
            </div>
          ),
        },
      ]
    : [];

  return (
    <Modal open={open} onCancel={onCancel} maskClosable={false} width="80vw" footer={null} title="Browse available certifications" className="browse-modal">
      <div className="browse-content">
        <div className="browse-body">
          <Row gutter={24}>
            <Col span={6} className="browse-col">
              <div className="col-title">
                <FilterOutlined /> Filter by
                <Button className="rst-button" type="secondary" icon={<CloseOutlined />} onClick={resetFilter}>
                  Reset
                </Button>
              </div>
              <div className="col-scroll">
                <Filter data={nonArchived} skillsGroup={rsSkillsGroup} toShow={handleFilterChange} resetFlag={resetFlag} />
              </div>
            </Col>

            <Col span={6} className="browse-col">
              <div className="col-title">
                <UnorderedListOutlined /> Certifications {filteredList.length}/{nonArchived.length}
              </div>
              <div className="col-scroll">
                <List
                  bordered
                  dataSource={filteredList}
                  renderItem={(item) => (
                    <List.Item>
                      <Checkbox checked={checkedList.includes(item.id)} onChange={() => onCheckboxChange(item.id, item)}>
                        {item.name}
                      </Checkbox>
                    </List.Item>
                  )}
                  locale={{ emptyText: <Spin /> }}
                />
              </div>
            </Col>

            <Col span={12} className="browse-col">
              {selectedCert && <h2 className="col-title">{selectedCert.name}</h2>}
              {selectedCert ? <Tabs defaultActiveKey="overview" items={tabsItems} className="tabs-content-scroll" /> : <div className="col-details-empty">Select a certification to see details.</div>}
            </Col>
          </Row>
        </div>
        <div className="browse-footer">
          <div className="footer-details">
            {checkedList.length === 1 ? '1 certification selected' : checkedList.length > 1 ? `${checkedList.length} certifications selected` : 'No certifications selected'}
            {checkedList.length > 0 && 
            <Link onClick={() => setCheckedList([])} className="clear-link">
              <CloseOutlined /> Clear
            </Link>}
          </div>
          <Button onClick={() => onLoad(checkedList)} type="primary" disabled={!checkedList.length}>
            Load Selected
          </Button>
          <Button onClick={onCancel}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CertificationBrowseCSPModalForm;
